import { Product } from '@components/ui/Product';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faGift } from '@fortawesome/pro-light-svg-icons';
import React, { useMemo } from 'react';
import { Order, PackagingDetail, PreparationWithProducts } from '../../models';
import type { UniqueBatchOption } from '../../utils/linePicksForOrder';
import { filterLinePicksForOrder, uniqueBatchesFromPicks } from '../../utils/linePicksForOrder';
import {
  computeOrderPackagingRequirements,
  packagingAssignKey,
  requirementUsesStationScan,
  sumAssignedForRequirement,
  type PackagingRequirement,
} from '../../utils/packagingRequirements';
import { AssignedPackagingScanDetail } from '../../utils/packingStationPackaging';
import { packingAssignKey } from '../../utils/packingAssignKey';

const packagingCategoryLabel = (req: PackagingRequirement) => {
  if (req.type === 'group') return 'Caja';
  if (req.category === 'marketing_item') return 'Marketing';
  if (req.category === 'own_box' || req.category === 'customer_box') return 'Caja';
  return 'Packaging';
};

const packagingCategoryClasses = (req: PackagingRequirement) => {
  if (req.type === 'group' || req.category === 'own_box' || req.category === 'customer_box') {
    return 'bg-bg-warning text-warning';
  }
  if (req.category === 'marketing_item') {
    return 'bg-bg-info text-info';
  }
  return 'bg-muted text-muted-foreground';
};

/** Badge solo con icono + etiqueta (Caja, Marketing, etc.) */
const PackagingTypeBadge = ({ requirement }: { requirement: PackagingRequirement }) => {
  const label = packagingCategoryLabel(requirement);
  const icon =
    requirement.type === 'group' || requirement.category === 'own_box' || requirement.category === 'customer_box'
      ? faBoxOpen
      : requirement.category === 'marketing_item'
        ? faGift
        : faBoxOpen;

  return (
    <span
      className={`mb-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${packagingCategoryClasses(requirement)}`}
      data-testid="packaging-type-badge"
    >
      <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5 shrink-0" />
      <span>{label}</span>
    </span>
  );
};

interface OrderProductsListProps {
  order: Order;
  assignedQuantities: Record<string, number>;
  assignedPackagingQuantities: Record<string, number>;
  assignedPackagingScanDetails?: Record<string, AssignedPackagingScanDetail>;
  packagings: PackagingDetail[];
  preparation: PreparationWithProducts | null;
  activeBatchIdByProduct: Record<number, number | null>;
  onActiveBatchChange: (productId: number, batchId: number | null) => void;
}

function ProductLinePicksBlock({
  productId,
  preparation,
  order,
  activeBatchIdByProduct,
  onActiveBatchChange,
}: {
  productId: number;
  preparation: PreparationWithProducts | null;
  order: Order;
  activeBatchIdByProduct: Record<number, number | null>;
  onActiveBatchChange: (productId: number, batchId: number | null) => void;
}) {
  const linePicksForOrder = useMemo(
    () => (preparation ? filterLinePicksForOrder(preparation.line_picks, preparation, order) : []),
    [preparation, order],
  );
  const picks = useMemo(
    () => linePicksForOrder.filter((p) => p.product_id === productId),
    [linePicksForOrder, productId],
  );
  const batchOpts = useMemo(() => uniqueBatchesFromPicks(picks), [picks]);

  const packRowShowsLotBadge = (b: UniqueBatchOption): boolean =>
    !!sanitizeBatchNumberForDisplay(b.batch_number) || Boolean(b.expiry_date);

  const selectLabel = (b: UniqueBatchOption): string => {
    const lot = sanitizeBatchNumberForDisplay(b.batch_number);
    if (lot && b.expiry_date) {
      return `${lot} — ${formatLineBatchDate(b.expiry_date)} (${b.pickedQty} uds.)`;
    }
    if (lot) return `${lot} (${b.pickedQty} uds.)`;
    if (b.expiry_date) return `Cad. ${formatLineBatchDate(b.expiry_date)} (${b.pickedQty} uds.)`;
    return `Lote #${b.batch_id} (${b.pickedQty} uds.)`;
  };

  if (picks.length === 0) {
    return <p className="text-xs text-muted-foreground mt-2">Sin datos de lotes pickeados para este producto.</p>;
  }

  if (batchOpts.length === 0) {
    return null;
  }

  const visibleBatches = batchOpts.filter((b) => packRowShowsLotBadge(b));

  return (
    <div className="mt-2 space-y-2">
      {visibleBatches.length > 0 ? (
        <>
          <p className="text-xs font-medium text-muted-foreground">Lotes pickeados</p>
          <ul className="space-y-1.5">
            {visibleBatches.map((b) => {
              const lot = sanitizeBatchNumberForDisplay(b.batch_number);
              return (
                <li
                  key={b.batch_id}
                  className="text-xs bg-muted/60 border border-border rounded-md px-2 py-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
                >
                  {lot ? <span className="font-medium text-foreground">{lot}</span> : null}
                  {b.expiry_date ? (
                    <span className="text-muted-foreground">Cad. {formatLineBatchDate(b.expiry_date)}</span>
                  ) : null}
                  <span className="text-muted-foreground ml-auto">{b.pickedQty} uds. pickeadas</span>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {batchOpts.length > 1 ? (
        <div className="pt-1">
          <label className="block text-xs mb-1.5 text-muted-foreground">Lote para el próximo escaneo</label>
          <Select
            value={activeBatchIdByProduct[productId]?.toString() ?? ''}
            onValueChange={(v) => onActiveBatchChange(productId, v ? Number(v) : null)}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Elige lote" />
            </SelectTrigger>
            <SelectContent>
              {batchOpts.map((b) => (
                <SelectItem key={b.batch_id} value={b.batch_id.toString()}>
                  {selectLabel(b)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground pt-0.5">Lote único: se asigna automáticamente al escanear.</p>
      )}
    </div>
  );
}

export const OrderProductsList: React.FC<OrderProductsListProps> = ({
  order,
  assignedQuantities,
  assignedPackagingQuantities,
  assignedPackagingScanDetails = {},
  packagings,
  preparation,
  activeBatchIdByProduct,
  onActiveBatchChange,
}) => {
  // Función auxiliar para construir URL completa de imagen (como en Packing original)
  const buildImageUrl = (imageHash: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  const processOrderProducts = () => {
    if (!order.lines) return [];

    const productsMap = new Map<string, any>();

    order.lines.forEach((line) => {
      if (line.pack && line.pack.lines) {
        // Es un pack - descomponer en productos individuales
        // Multiplicar la cantidad de cada producto por la cantidad de packs
        const packQuantity = line.qty || 1; // Cantidad de packs

        line.pack.lines.forEach((packLine) => {
          const code = (packLine.product?.code || '').trim();
          // Cantidad total = cantidad del producto en el pack × cantidad de packs
          const totalQuantity = (packLine.qty || 1) * packQuantity;

          // Para packs, incluir productCode en la key
          const lineKey = packingAssignKey(order.id, line.id, code);
          const assigned = assignedQuantities[lineKey] || 0;

          if (productsMap.has(code)) {
            // Si ya existe, sumar la cantidad esperada y la asignada
            const existing = productsMap.get(code);
            existing.expectedQuantity += totalQuantity;
            existing.assignedQuantity += assigned;
            existing.isCompleted = existing.assignedQuantity >= existing.expectedQuantity;
            // Guardar las líneas para tracking
            existing.lines.push({ lineId: line.id, productCode: code, qty: totalQuantity, assigned, packQuantity });
          } else {
            // Si no existe, crear nuevo
            productsMap.set(code, {
              id: `${order.id}-${packLine.product_id}-pack`,
              productId: packLine.product_id,
              name: packLine.product?.name || `Producto ID: ${packLine.product_id}`,
              code: code,
              image: buildImageUrl(packLine.product?.image_hash || ''),
              expectedQuantity: totalQuantity,
              assignedQuantity: assigned,
              isCompleted: assigned >= totalQuantity,
              isFromPack: false, // No mostrar badge de pack
              lines: [{ lineId: line.id, productCode: code, qty: totalQuantity, assigned, packQuantity }],
            });
          }
        });
      } else {
        // Es un producto individual
        const code = (line.product?.code || '').trim();
        const lineKey = packingAssignKey(order.id, line.id, code);
        const assigned = assignedQuantities[lineKey] || 0;

        if (productsMap.has(code)) {
          // Si ya existe, sumar la cantidad esperada y la asignada
          const existing = productsMap.get(code);
          existing.expectedQuantity += line.qty || 1;
          existing.assignedQuantity += assigned;
          existing.isCompleted = existing.assignedQuantity >= existing.expectedQuantity;
          // Guardar las líneas para tracking (sin marcar como mixed, los packs no existen en la vista)
          existing.lines.push({ lineId: line.id, qty: line.qty || 1, assigned });
        } else {
          // Si no existe, crear nuevo
          productsMap.set(code, {
            id: `${order.id}-${line.product_id}-single`,
            productId: line.product_id,
            name: line.product?.name || `Producto ID: ${line.product_id}`,
            code: code,
            image: buildImageUrl(line.product?.image_hash || ''),
            expectedQuantity: line.qty || 1,
            assignedQuantity: assigned,
            isCompleted: assigned >= (line.qty || 1),
            isFromPack: false,
            lines: [{ lineId: line.id, qty: line.qty || 1, assigned }],
          });
        }
      }
    });

    // Convertir el Map a array
    return Array.from(productsMap.values());
  };

  const products = processOrderProducts();
  const packagingItems = useMemo(() => {
    const requirements = computeOrderPackagingRequirements(order, packagings);
    return requirements.map((req) => {
      const assigned = sumAssignedForRequirement(req, order.id, assignedPackagingQuantities);
      const scannedOptions = (req.options || []).filter(
        (opt) => (assignedPackagingQuantities[packagingAssignKey(order.id, opt.packagingId)] || 0) > 0,
      );
      let scannedOption = scannedOptions[0];
      if (!scannedOption) {
        for (const memberId of req.members) {
          const detailKey = packagingAssignKey(order.id, memberId);
          if ((assignedPackagingQuantities[detailKey] || 0) > 0 && assignedPackagingScanDetails[detailKey]) {
            scannedOption = assignedPackagingScanDetails[detailKey];
            break;
          }
        }
      }
      return {
        id: `packaging-${req.key}`,
        itemType: 'packaging' as const,
        name: scannedOption?.name || req.name || 'Packaging',
        code: scannedOption?.code,
        image: scannedOption?.imageUrl || '',
        expectedQuantity: req.qty,
        assignedQuantity: assigned,
        isCompleted: assigned >= req.qty,
        requirement: req,
        scanHint: requirementUsesStationScan(req)
          ? 'Escanea la ubicación del puesto'
          : req.type === 'group'
            ? 'Escanea cualquier caja del grupo'
            : 'Escaneo exacto requerido',
      };
    });
  }, [order, packagings, assignedPackagingQuantities, assignedPackagingScanDetails]);

  const combinedItems = [...products, ...packagingItems];
  const completedProducts = combinedItems.filter((p) => p.isCompleted);
  const pendingProducts = combinedItems.filter((p) => !p.isCompleted);

  if (products.length === 0) {
    return (
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="text-center py-8">
          <div className="text-muted-foreground mb-4">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No hay items</h3>
          <p className="text-sm text-muted-foreground">Este pedido no tiene productos o packagings asignados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Productos pendientes */}
      {pendingProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-orange-600 dark:text-orange-400">Por empaquetar</span>
              <span className="text-sm text-muted-foreground">({pendingProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {pendingProducts.map((product) => {
              const remaining = product.expectedQuantity - product.assignedQuantity;
              const progressPercentage = (product.assignedQuantity / product.expectedQuantity) * 100;
              const isPackaging = product.itemType === 'packaging';

              return (
                <div
                  key={`${product.id}-${product.code}`}
                  className="bg-background border border-border rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {isPackaging && product.requirement ? (
                        <PackagingTypeBadge requirement={product.requirement} />
                      ) : null}
                      <Product
                        id={Number.isNaN(parseInt(product.id)) ? -1 : parseInt(product.id)}
                        src={product.image}
                        name={product.name}
                        code={product.code}
                        acronym={product.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      {isPackaging && product.scanHint && product.assignedQuantity === 0 ? (
                        <p className="text-xs text-muted-foreground mt-1">{product.scanHint}</p>
                      ) : null}
                      {!isPackaging ? (
                        <ProductLinePicksBlock
                          productId={product.productId}
                          preparation={preparation}
                          order={order}
                          activeBatchIdByProduct={activeBatchIdByProduct}
                          onActiveBatchChange={onActiveBatchChange}
                        />
                      ) : null}
                    </div>

                    {/* Información de cantidades */}
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {product.assignedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                      </div>

                      <div className="text-xs text-muted-foreground">Faltan {remaining}</div>

                      {/* Barra de progreso */}
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Productos completados */}
      {completedProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">Empaquetados</span>
              <span className="text-sm text-muted-foreground">({completedProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {completedProducts.map((product) => {
              const isPackaging = product.itemType === 'packaging';

              return (
                <div
                  key={`${product.id}-${product.code}`}
                  className="bg-green-50/30 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {isPackaging && product.requirement ? (
                        <PackagingTypeBadge requirement={product.requirement} />
                      ) : null}
                      <Product
                        id={Number.isNaN(parseInt(product.id)) ? -1 : parseInt(product.id)}
                        src={product.image}
                        name={product.name}
                        code={product.code}
                        acronym={product.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      {isPackaging && product.scanHint && product.assignedQuantity === 0 ? (
                        <p className="text-xs text-muted-foreground mt-1">{product.scanHint}</p>
                      ) : null}
                      {!isPackaging ? (
                        <ProductLinePicksBlock
                          productId={product.productId}
                          preparation={preparation}
                          order={order}
                          activeBatchIdByProduct={activeBatchIdByProduct}
                          onActiveBatchChange={onActiveBatchChange}
                        />
                      ) : null}
                    </div>

                    {/* Información de cantidades completadas */}
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {product.assignedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                      </div>

                      <div className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Completado</div>

                      {/* Barra de progreso completada */}
                      <div className="w-16 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: '100%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resumen del pedido */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Progreso del pedido:</span>
          <span className="font-medium text-foreground">
            {completedProducts.length} de {combinedItems.length} items empaquetados
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total de unidades:</span>
          <span className="font-medium text-foreground">
            {combinedItems.reduce((sum, p) => sum + p.assignedQuantity, 0)} de{' '}
            {combinedItems.reduce((sum, p) => sum + p.expectedQuantity, 0)} unidades
          </span>
        </div>

        <div className="mt-2">
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${combinedItems.length > 0 ? (completedProducts.length / combinedItems.length) * 100 : 0}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};
